// Reports already uses fetch; keep the existing Token authentication convention.
const baseUrl = () => (process.env.REACT_APP_DJANGO_API_BASE_URL || '').replace(/\/$/, '');

export class ExportApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

function messages(value) {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(messages).filter(Boolean).join(' ');
  if (value && typeof value === 'object') return Object.values(value).map(messages).filter(Boolean).join(' ');
  return '';
}

async function request(url, { signal, authenticated = true, timeout = 15000, ...options } = {}) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; abort(); }, timeout);
  try {
    const headers = { ...options.headers };
    if (authenticated) {
      const token = localStorage.getItem('token');
      if (!token) throw new ExportApiError('Please sign in again to access your export.', 401);
      headers.Authorization = `Token ${token}`;
    }
    const response = await fetch(url, { ...options, headers, signal: controller.signal });
    if (!response.ok) {
      let body;
      try { body = await response.json(); } catch (_) { /* Proxy errors may be HTML. */ }
      const detail = body?.error || body?.detail || body?.non_field_errors || body;
      throw new ExportApiError(messages(detail) || `Export request failed (${response.status}). Please try again.`, response.status);
    }
    // Keep the timeout/abort active while reading the response body, too.
    return options.responseType === 'blob'
      ? { blob: await response.blob(), disposition: response.headers.get('Content-Disposition') }
      : await response.json();
  } catch (error) {
    if (timedOut && !signal?.aborted) throw new ExportApiError('The export service did not respond in time. Please try again.');
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}

export const createManifestExport = (dates, signal) => request(`${baseUrl()}/api/manifests/export/`, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dates), signal,
});

export const getManifestExport = (id, signal) => request(`${baseUrl()}/api/manifests/export/${encodeURIComponent(id)}/`, {
  method: 'GET', cache: 'no-store', signal,
});

export function exportFilename(disposition, fallback) {
  let filename;
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try { filename = decodeURIComponent(encoded[1].trim()); } catch (_) { /* Fall back to filename=. */ }
  }
  if (!filename) filename = disposition?.match(/filename="([^"]+)"|filename=([^;]+)/i)?.slice(1).find(Boolean)?.trim();
  return Array.from((filename || fallback).replace(/[\\/]/g, '_'))
    .map(character => character.charCodeAt(0) < 32 ? '_' : character).join('');
}

export async function downloadManifestExport(job, signal) {
  const origin = new URL(baseUrl() || window.location.origin, window.location.origin);
  const fallback = new URL(`/api/manifests/export/${encodeURIComponent(job.id)}/download/`, origin);
  const url = new URL(job.download_url || fallback.href, `${origin.href.replace(/\/$/, '')}/`);
  if (!['https:', 'http:'].includes(url.protocol)) throw new ExportApiError('The export download URL is invalid.');
  let result;
  try {
    result = await request(url.href, {
      method: 'GET', signal, responseType: 'blob', timeout: 120000,
      authenticated: url.origin === origin.origin,
    });
  } catch (error) {
    if (signal?.aborted || url.origin === origin.origin) throw error;
    // Expired signed URLs or external CORS failures can use the protected endpoint.
    result = await request(fallback.href, { method: 'GET', signal, responseType: 'blob', timeout: 120000 });
  }
  return {
    blob: result.blob,
    filename: exportFilename(result.disposition, `manifest_${job.start_date}_to_${job.end_date}.csv`),
  };
}
